import React from 'react';
import { ImageCardProps } from '..';
import { Description, Label, StyledCTAButton, CTAEditorModal, Title, Wrapper } from './styles';
import usePageBlock from 'hooks/use-page-block';
import { useFocusArea } from 'hooks';
import { useSelector } from 'react-redux';
import { isEditMode } from 'services/admin';
import { getAdminBarWidth } from 'services/admin/selectors';
import { PREDEFINED_TOOLBAR_BUTTONS } from 'components/ui/RichTextEditorV2/utils';
import { useImageGalleryContext } from '../../ImageGalleryContext/ImageGalleryContext';

interface TextContentProps {
  imageCard: ImageCardProps;
}

type EditableStringPath = (
  'label' |
  'title' |
  'description' |
  'cta.variant' |
  'cta.behavior' |
  'cta.text' |
  'cta.height.desktop' |
  'cta.height.mobile'
);

const TextContent: React.FC<TextContentProps> = ({ imageCard }) => {
  const isEditing = useSelector(isEditMode);
  const adminBarWidth = useSelector(getAdminBarWidth);
  const [isFocused, setIsFocused] = React.useState(false);
  const {
    hasTextContent,
    imagesPerRow,
    editImageCard,
    showCta,
    showDescription,
    showLabel,
    showTitle,
    gridTextAlignment,
    imageLayout,
    background,
  } = useImageGalleryContext();

  const { handleChange } = usePageBlock<EditableStringPath, ImageCardProps>({
    item: imageCard,
    onChange: editImageCard,
  });

  const ref = useFocusArea({
    onExit: () => setIsFocused(false),
    active: isFocused,
  });

  const handleDisablePropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFocused(true);
  };

  const renderLabel = React.useCallback(() => {
    if (!showLabel) {
      return null;
    }

    return (
      <Label
        imagesPerRow={imagesPerRow}
        blockTitleKey="ADMIN_LABEL"
        onChange={handleChange('label')}
        data={imageCard.label}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.titleEditor}
        data-testid="image-card-label"
      />
    );
  }, [showLabel, imagesPerRow, handleChange, imageCard.label]);

  const renderTitle = React.useCallback(() => {
    if (!showTitle) {
      return null;
    }

    return (
      <Title
        imagesPerRow={imagesPerRow}
        blockTitleKey="ADMIN_LABEL_TITLE"
        onChange={handleChange('title')}
        data={imageCard.title}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.titleEditor}
        data-testid="image-card-title"
        htmlTag="h3"
      />
    );
  }, [showTitle, imagesPerRow, handleChange, imageCard.title]);

  const renderDescription = React.useCallback(() => {
    if (!showDescription) {
      return null;
    }

    return (
      <Description
        imagesPerRow={imagesPerRow}
        blockTitleKey="ADMIN_LABEL_DESCRIPTION"
        onChange={handleChange('description')}
        data={imageCard.description}
        toolbarButtons={PREDEFINED_TOOLBAR_BUTTONS.titleEditor}
        data-testid="image-card-description"
      />
    );
  }, [showDescription, imagesPerRow, handleChange, imageCard.description]);

  const handleChangeBehavior = React.useCallback((behavior: ImageCardProps['cta']['behavior']) => {
    editImageCard({
      ...imageCard,
      cta: {
        ...imageCard.cta,
        behavior,
      },
      link: behavior.type === 'openLink' ? behavior.url : imageCard.link,
    });
  }, [editImageCard, imageCard]);

  const renderCta = React.useCallback(() => {
    if (!showCta) {
      return null;
    }

    return (
      <CTAEditorModal
        modalTitleKey="ADMIN_LABEL_CALL_TO_ACTION"
        blockTitleKey="ADMIN_LABEL_CALL_TO_ACTION"
        onChangeVariant={handleChange('cta.variant')}
        onChangeBehavior={handleChangeBehavior}
        onChangeText={handleChange('cta.text')}
        onChangeDesktopHeight={handleChange('cta.height.desktop')}
        onChangeMobileHeight={handleChange('cta.height.mobile')}
        cta={imageCard.cta}
      >
        {(text) => (
          <StyledCTAButton
            variant={imageCard.cta.variant}
            imagesPerRow={imagesPerRow}
            containerBackgroundColor={background.color}
            height={imageCard.cta.height}
          >
            {text}
          </StyledCTAButton>
        )}
      </CTAEditorModal>
    );
  }, [showCta, background.color, imageCard.cta, imageCard.link, handleChange, imagesPerRow, isEditing, adminBarWidth]);

  if (!hasTextContent) {
    return null;
  }

  return (
    <Wrapper
      // @ts-ignore
      ref={ref}
      isFocused={isFocused}
      onClick={handleDisablePropagation}
      alignment={gridTextAlignment}
      imageLayout={imageLayout}
    >
      {renderLabel()}
      {renderTitle()}
      {renderDescription()}
      {renderCta()}
    </Wrapper>
  );
};

export default TextContent;
